import * as React from "react";
import ReactDOM from "react-dom";
import { PopupPositionsType } from "react-ui";

import { arrayHasAtLeastOneItem, isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import { PicklistDataItem, PicklistQueryResponse } from "../../../Engine/Core/Picklists/PicklistTypes";
import { MetricsValueSenderActions } from "../../Internals/Metrics/MetricsValueSender";
import { DataPasteContext } from "../../MultiControls/MultiLine/TableDataPasteContext";
import { pullContext } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { ExcelPasteUtils } from "../../MultiControls/MultiLine/ExcelPasteUtils";
import { IScrollToContext, scrollToContextTypes } from "../../Internals/Contexts/ScrollToContext";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { TestPageObjectHandle } from "../../Services/TestPageObjects/TestPageObjectHandle";
import { isDOMRectInView } from "../../../Helpers/BrowserHelpers";
import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";
import { Text } from "../../ValueViewers/Text/Text";

import { BasePicklist } from "./BasePicklist/BasePicklist";
import { BasePicklistSearchProps, PicklistDisplayType } from "./BasePicklist/BasePicklistProps";
import {
    getPickListItemsBySearchQuery,
    getPickListItemsByValue,
    PicklistControlDependency,
} from "./Common/PicklistItemProvider";
import { changeRelatedFields } from "./Common/changeRelatedFields";

export interface PicklistProps extends ControlValidationProps, ControlTooltipProps, BasePicklistSearchProps {
    bindingPath: AbsoluteResolvedModelPath;
    value: string;
    onChange: (items?: PicklistDataItem[] | PicklistDataItem, fieldName?: string) => Promise<void>;
    align?: "left" | "center" | "right";
    columnsWidth?: number[];
    description?: string;
    empty?: string;
    dependencies: PicklistControlDependency[];
    gId?: string;
    gPaths?: string[];
    headers?: string[];
    isTooltipEnabled: boolean;
    tooltipPosition?: PopupPositionsType;
    mapChangesField: string[];
    narrowButton?: boolean;
    footer?: React.ReactNode;
    needDescription?: boolean;
    normativeHelp: [] | [string, string];
    overwriteOnInit?: boolean;
    placeholder: string;
    saveDescription?: boolean;
    setDependence: (x1: string, x2: string) => void;
    title: string;
    type: PicklistDisplayType;
    width?: string | number;
    multiple?: boolean;
    metrics: MetricsValueSenderActions;
    partialHelpTip?: string;
    children?: readonly string[];
    maskPath?: string;
    setPicklistDataItem?: (values: undefined | PicklistDataItem | Array<undefined | PicklistDataItem>) => void;
    isLink?: boolean;
    isReadOnly?: boolean;
    onClick?: () => void;
    "data-tid"?: string;
    descriptionInModal: string | React.ReactNode;
    hintFields?: string[];
    hintWidth?: number;
    isEnableCaseIndependentValidation?: boolean;
    disabled?: boolean;
}

export class Picklist extends React.PureComponent<PicklistProps> {
    public static defaultProps = {
        fields: ["code", "name"],
        limit: 100,
    };
    private totalItemCount = 0;
    private isFirstRequest = true;
    private searchTextLength = 0;

    public static contextTypes = scrollToContextTypes;

    public render(): JSX.Element {
        if (this.props.isReadOnly) {
            return (
                <Text
                    value={this.props.value}
                    data-tid={this.props["data-tid"]}
                    bindingPath={this.props.bindingPath}
                    onClick={this.props.onClick}
                    isLink={this.props.isLink}
                />
            );
        }
        return (
            <TestPageObjectHandle
                id={this.props.bindingPath}
                pageObject={{
                    getType: () => "Picklist",
                    getValue: () => this.props.value,
                    setValue: async (value: string) => {
                        const picklistQueryResponse = await this.query(value);
                        await this.onChangeValue(value, picklistQueryResponse);
                    },
                    isFocused: () =>
                        document.activeElement ===
                        toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.querySelector("input"),
                    isInView: () =>
                        isDOMRectInView(toHtmlElementOrNull(ReactDOM.findDOMNode(this))?.getBoundingClientRect()),
                }}>
                <DataPasteContext.Consumer>
                    {handlePaste => (
                        <FocusManagementObserver onRequestFocus={this.handleRequestFocus}>
                            <BasePicklist
                                onChange={this.onChangeValue}
                                query={this.query}
                                align={this.props.align}
                                handleInputValueChange={this.sendMetricOnValueChange}
                                handleInputValueSave={this.saveMetricOnValueChange}
                                columnsWidth={this.props.columnsWidth}
                                description={this.props.saveDescription ? this.props.description : undefined}
                                empty={this.props.empty}
                                data-tid={this.props["data-tid"]}
                                error={this.props.error}
                                fields={this.props.fields}
                                headers={this.props.headers}
                                isTooltipEnabled={this.props.isTooltipEnabled}
                                tooltipPosition={this.props.tooltipPosition}
                                narrowButton={this.props.narrowButton}
                                needDescription={this.props.needDescription}
                                normativeHelp={this.props.normativeHelp}
                                overwriteOnInit={this.props.overwriteOnInit}
                                placeholder={this.props.placeholder}
                                searchByField={this.props.searchByField}
                                searchFields={this.props.searchFields}
                                title={this.props.title}
                                type={this.props.type}
                                value={this.props.value}
                                warning={this.props.warning}
                                width={this.props.width}
                                multiple={this.props.multiple ?? false}
                                partialHelpTip={this.props.partialHelpTip}
                                footer={this.props.footer}
                                descriptionInModal={this.props.descriptionInModal}
                                onModalOpen={this.handleOnOpen}
                                handleModalChange={this.handleModalChange}
                                onPaste={ExcelPasteUtils.WrapHandlePaste(handlePaste, this.props.bindingPath)}
                                hintFields={this.props.hintFields}
                                hintWidth={this.props.hintWidth}
                                onQueryPicklist={this.props.onQueryPicklist}
                                limit={this.props.limit}
                                isEnableCaseIndependentValidation={this.props.isEnableCaseIndependentValidation}
                                disabled={this.props.disabled}
                            />
                        </FocusManagementObserver>
                    )}
                </DataPasteContext.Consumer>
            </TestPageObjectHandle>
        );
    }

    private readonly handleRequestFocus = (): void => {
        const thisNode = toHtmlElementOrNull(ReactDOM.findDOMNode(this));
        if (thisNode != undefined) {
            const input = thisNode.querySelector("input");
            const button = thisNode.querySelector("button");

            if (input != undefined) {
                this.getContext().scrollTo(input);
                input.focus();
                input.select();
            } else if (button != undefined) {
                this.getContext().scrollTo(button);
                button.focus();
            }
        }
    };

    private readonly saveMetricOnValueChange = (value: string): void => {
        this.props.metrics.saveValue(value);
    };

    private readonly sendMetricOnValueChange = (value: string): void => {
        this.props.metrics.sendValue(
            this.props.bindingPath,
            value,
            this.searchTextLength,
            this.totalItemCount,
            this.props.limit,
            this.props.gId,
            "Picklist"
        );
    };

    public readonly query = async (searchQuery: string = ""): Promise<PicklistQueryResponse> => {
        const response = await getPickListItemsBySearchQuery(searchQuery, this.props);

        if (this.isFirstRequest) {
            this.isFirstRequest = false;
            this.totalItemCount = response.suitableCount;
        }

        this.searchTextLength = searchQuery.length;
        return response;
    };

    public onChangeValue = async (value: string, queryResponse: PicklistQueryResponse): Promise<void> => {
        if (this.props.multiple) {
            let instances: string[];
            if (value) {
                instances = value
                    .split(",")
                    .map(val => val.trim())
                    .filter(val => val);
            } else {
                instances = [""];
            }

            const picklistQueryResponse = await getPickListItemsByValue(
                value,
                this.props,
                this.props.isEnableCaseIndependentValidation,
                false
            );
            const itemsByValues = instances.map(instance => {
                const itemByValue = picklistQueryResponse.items.find(
                    item => item[this.props.searchByField]?.toString() === instance
                );
                return itemByValue ?? { [this.props.searchByField]: instance };
            });
            this.props.setPicklistDataItem?.(itemsByValues);
            await this.props.onChange(itemsByValues.filter(isNotNullOrUndefined));
        } else {
            // NOTE В случае, если зависимое поле оказалось пустое, берем первый найденный элемент
            const itemByValue = queryResponse.items.find(item => item[this.props.searchByField]?.toString() === value);
            await this.props.onChange(itemByValue ?? { [this.props.searchByField]: value }, this.props.searchByField);
            this.props.setPicklistDataItem?.(itemByValue);
        }

        changeRelatedFields(value, queryResponse.items, this.props);
    };

    public handleModalChange = async (items: PicklistDataItem[]) => {
        let valueToMetrics = "";

        if (this.props.multiple) {
            for (const fieldName of this.props.mapChangesField) {
                valueToMetrics = items.map(item => item[fieldName]).join(", ");
                await this.props.onChange(items);
            }
            this.props.setPicklistDataItem?.(items);
        } else {
            if (!arrayHasAtLeastOneItem(items)) {
                return;
            }
            const [item] = items;
            this.props.dependencies.forEach(dependence => {
                if (dependence.path.toLegacyPath() === this.props.bindingPath.toLegacyPath()) {
                    // NOTE по хорошему тут нужно вычислять то поле, которое уезжает в { value: value} пиклиста
                    // Но сейчас это впихивать очень долго, и я посмотрел на все пиклисты, не нашел там другого значения чем code
                    valueToMetrics = item["code"]?.toString() ?? "";
                }
            });
            for (const fieldName of this.props.mapChangesField) {
                await this.props.onChange(item, fieldName);
            }
            this.props.setPicklistDataItem?.(item);
        }

        if (this.props.metrics) {
            this.props.metrics.sendValue(
                this.props.bindingPath,
                valueToMetrics,
                this.searchTextLength,
                this.totalItemCount,
                this.props.limit,
                this.props.gId,
                "Picklist"
            );
        }
    };

    public handleOnOpen = () => {
        if (this.props.metrics) {
            this.props.metrics.saveValue(this.props.value);
        }
    };

    private getContext(): IScrollToContext {
        return pullContext<IScrollToContext>(this);
    }
}
